<template>
<div>

  <!-- Banner Container - Conditionally clickable based on button text -->
  <div 
    v-if="!hasButtonText"
    @click="handleBannerClick"
    class="highlight-banner relative rounded-xl overflow-hidden cursor-pointer" 
    :class="getAspectRatioClass()"
    :style="{
      border: '1px solid #d1d5db',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
    }"
  >
    <!-- Background Image -->
    <img 
      :src="banner.imageUrl" 
      :alt="banner.title" 
      class="w-full h-full object-cover absolute inset-0"
    />
    
    <!-- Stronger Gradient Overlay for text readability -->
    <div 
      class="absolute inset-0 gradient-overlay"
      :style="gradientOverlayStyle"
    ></div>
    
    <!-- Full-width Content (when no button) - Left aligned -->
    <div class="absolute bottom-0 left-0 w-full p-4">
      <div class="text-left">
        <h3 class="text-neutral-100 font-medium text-lg">{{ banner.title }}</h3>
        <p class="text-gray-200 text-xs">{{ banner.subtitle }}</p>
      </div>
    </div>
  </div>

  <!-- Banner with Button (when button text exists) -->
  <div 
    v-else
    class="highlight-banner relative rounded-xl overflow-hidden" 
    :class="getAspectRatioClass()"
    :style="{
      border: '1px solid #d1d5db',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
    }"
  >
    <!-- Background Image -->
    <img 
      :src="banner.imageUrl" 
      :alt="banner.title" 
      class="w-full h-full object-cover absolute inset-0"
    />
    
    <!-- Stronger Gradient Overlay for text readability -->
    <div 
      class="absolute inset-0 gradient-overlay"
      :style="gradientOverlayStyle"
    ></div>
    
    <!-- Content Container with Button -->
    <div class="absolute bottom-0 left-0 w-full p-4 flex flex-col md:flex-row md:items-end justify-between">
      <!-- Text Content -->
      <div class="mb-4 md:mb-0">
        <h3 class="text-neutral-500 font-medium text-lg">{{ banner.title }}</h3>
        <p class="text-neutral-500 text-xs">{{ banner.subtitle }}</p>
      </div>
      
      <!-- CTA Button (styled like Callback.vue) -->
      <a 
        :href="banner.buttonUrl" 
        class="inline-block py-2 px-4 text-base rounded-md transition-colors duration-200 text-center font-medium"
        :style="{ 
          backgroundColor: buttonColor || '#3b82f6', 
          color: textColor || '#ffffff'
        }"
        target="_blank"
        rel="noopener"
      >
        {{ banner.buttonText }}
      </a>
    </div>
  </div>
</div>
</template>

<script>
export default {
  name: 'HighlightBanner',
  props: {
    banner: {
      type: Object,
      required: true,
      default: () => ({
        title: 'Neue Ankündigung',
        subtitle: 'Erfahre mehr über unsere neuesten Updates',
        imageUrl: 'https://images.unsplash.com/photo-1605379399642-870262d3d051',
        buttonText: 'Mehr erfahren',
        buttonUrl: '#'
      })
    },
    buttonColor: {
      type: String,
      default: null
    },
    textColor: {
      type: String,
      default: null
    },
    aspectRatio: {
      type: String,
      default: 'rectangle', // Default aspect ratio
      validator: (value) => ['square', 'rectangle', 'wide', 'cinema'].includes(value)
    }
  },
  computed: {
    // Check if button text exists and is not empty
    hasButtonText() {
      return this.banner.buttonText && this.banner.buttonText.trim() !== '';
    },
    
    // Stronger gradient overlay - darker and more pronounced
    gradientOverlayStyle() {
      return {
        background: 'linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.6) 35%, rgba(0, 0, 0, 0.3) 60%, transparent 85%)'
      };
    }
  },
  methods: {
    // Handle full banner click when no button text
    handleBannerClick() {
      if (this.banner.buttonUrl && this.banner.buttonUrl !== '#') {
        // Open link in new tab
        window.open(this.banner.buttonUrl, '_blank', 'noopener');
      }
    },
    
    getAspectRatioClass() {
      const ratios = {
        'square': 'aspect-square',        // 1:1 ratio
        'rectangle': 'aspect-video',      // 16:9 ratio (default)
        'wide': 'aspect-[21/9]',         // 21:9 ultra-wide
        'cinema': 'aspect-[2.35/1]'       // 2.35:1 cinematic
      };
      
      return ratios[this.aspectRatio] || ratios['rectangle'];
    }
  }
};
</script>

<style scoped>
/* Fallback aspect ratios for older browsers */
.aspect-square {
  aspect-ratio: 1 / 1;
}

.aspect-video {
  aspect-ratio: 16 / 9;
}

.aspect-\[21\/9\] {
  aspect-ratio: 21 / 9;
}

.aspect-\[2\.35\/1\] {
  aspect-ratio: 2.35 / 1;
}

/* Stronger gradient overlay - darker and more pronounced */
.gradient-overlay {
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.6) 35%, rgba(0, 0, 0, 0.3) 60%, transparent 85%) !important;
}
</style>